import { Button, message, Select, Switch } from "antd";
import styles from "./TransactionSetting.module.scss";
import eyeSlash from "../../../../../../assets/icons/eye-slash-purple.svg";
import eye from "../../../../../../assets/icons/eye-purple.svg";
import editIcon from "../../../../../../assets/icons/edit-icon.svg";
import { useEffect, useState } from "react";
import ConfirmModal from "./modals/ConfirmModal";
import EditTransferLimit from "./modals/EditTransferLimit";
import TransactionsTable from "./TransactionsTable";
import { useFetchOutwardTransferProviders } from "../../../../../../custom-hooks/useFetchOutwardTransferProviders";
import { useFetchEasyPayBanks } from "../../../../../../custom-hooks/useFetchEasyPayBanks";
import {
  ChannelType,
  useFetchProviderBalanceEnquiry,
} from "../../../../../../custom-hooks/useFetchProviderBalanceEnquiry";
import { formatMoney } from "../../../../../../helpers/formatter";
import Loader from "../../../../../../NewComponents/Loader/Loader.component";
import * as ajaxEndpoints from "../../../../../../api/ajax-endpoints";
import { patchData } from "../../../../../../newApis/dotNetApiMethods";
import { useQueryCache } from "react-query";

const { Option } = Select;

type ProviderAccount = {
  id?: number;
  accountName?: string;
  accountNumber?: string;
  bankCode?: string;
  bvn?: string | null;
  providerId?: number;
  isSetProviderAccount?: boolean;
  isDisabled?: boolean;
};

type SelectedProviderAccount = {
  id?: number;
  accountName?: string;
  accountNumber?: string;
  bankCode?: string;
  bankName?: string;
  bvn?: string | null;
  providerId?: number;
  isSetProviderAccount?: boolean;
  isDisabled?: boolean;
};

export type Provider = {
  id?: number;
  name?: string;
  maximumTransferAmount?: number;
  minimumTransferAmount?: number;
  isCurrentOutwardTransferProvider?: boolean;
  isCurrentNameEnquiryProvider?: boolean;
  currentProviderAccountId?: number | null;
  thresholdAmount?: number;
  accounts?: ProviderAccount[];
};

export type Bank = {
  bankCode: string;
  bankName: string;
  pngLogoUrl: string | null;
  svgLogoUrl: string | null;
};

const TransactionSettings = () => {
  const [openSetCurrentProviderModal, setOpenSetCurrentProviderModal] =
    useState(false);
  const [openEditLimitModal, setOpenEditLimitModal] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [selectedProviderDetails, setSelectedProviderDetails] =
    useState<Provider | null>(null);
  const [isReveal, setIsReveal] = useState(false);
  const [easyPayBankList, setEasyPayBankList] = useState<Bank[]>([]);
  const [selectedBankCode, setSelectedBankCode] = useState<string | null>(null);
  const [selectedAccountInfo, setSelectedAccountInfo] =
    useState<SelectedProviderAccount | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const queryCache = useQueryCache();
  const { data: transferProviders, isLoading: isLoadingTransferProviders } =
    useFetchOutwardTransferProviders();

  const {
    data: easyPayFinancialInstitutions,
    isLoading: isLoadingEasyPayFinancialInstitutions,
  } = useFetchEasyPayBanks();

  const { data: accountBalanceData, isLoading: isLoadingAccountBalance } =
    useFetchProviderBalanceEnquiry({
      bankCode: selectedProviderDetails?.accounts?.[0]?.bankCode || "",
      accountNumber:
        selectedProviderDetails?.accounts?.[0]?.accountNumber || "",
      channelCode: ChannelType.WEB,
      isReveal: isReveal,
    });

  useEffect(() => {
    if (!transferProviders?.data?.providers || !selectedProviderDetails?.id)
      return;

    const matchedProvider = transferProviders.data.providers.find(
      (provider: Provider) => provider.id === selectedProviderDetails.id,
    );

    if (!matchedProvider) return;

    setSelectedProviderDetails(matchedProvider);
  }, [transferProviders, selectedProviderDetails?.id]);

  useEffect(() => {
    if (
      selectedProviderDetails?.accounts?.length === 0 ||
      !easyPayFinancialInstitutions?.data
    ) {
      setEasyPayBankList([]);
      return;
    }

    const accountBankCodes = new Set(
      selectedProviderDetails?.accounts?.map((account) => account.bankCode),
    );
    const matchedBanks = easyPayFinancialInstitutions.data.filter(
      (bank: Bank) => accountBankCodes.has(bank.bankCode),
    );

    setEasyPayBankList(matchedBanks);
  }, [selectedProviderDetails?.accounts, easyPayFinancialInstitutions]);

  useEffect(() => {
    if (!selectedProviderDetails?.id) return;
    setSelectedBankCode(selectedProviderDetails?.accounts?.[0]?.bankCode || "");
  }, [selectedProviderDetails?.id, selectedProviderDetails?.accounts]);

  useEffect(() => {
    if (!selectedProviderDetails?.id) return;
    const selectedAccount = selectedProviderDetails.accounts?.find(
      (account) => account.bankCode === selectedBankCode,
    );

    const selectedAccountBankName = easyPayBankList.find(
      (bank) => bank.bankCode === selectedBankCode,
    );

    if (selectedAccount) {
      setSelectedAccountInfo({
        ...selectedAccount,
        bankName: selectedAccountBankName?.bankName,
      });
    } else {
      setSelectedAccountInfo(null);
    }
  }, [
    selectedBankCode,
    selectedProviderDetails?.id,
    selectedProviderDetails?.accounts,
  ]);

  const handleSetCurrentProvider = async () => {
    if (!selectedProviderDetails?.id) return;
    setIsLoading(true);
    try {
      const response = await patchData(ajaxEndpoints.SET_CURRENT_PROVIDER, {
        transferProviderId: selectedProviderDetails?.id,
      });

      if (response.status === 200) {
        await queryCache.invalidateQueries(["fetchOutwardTransferProviders"]);
        message.success(response.data.message);
        setOpenSetCurrentProviderModal(false);
      }
    } catch (error) {
      console.log(error);
      message.error(error.message || "Failed to set current provider");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full mt-3">
      {showTransactions ? (
        <TransactionsTable setShowTransactions={setShowTransactions} />
      ) : (
        <>
          <header className="d-flex justify-content-between align-items-center w-full">
            <h2>Payment Provider</h2>
            <Button
              type="primary"
              className="p-a-2"
              onClick={() => setShowTransactions(true)}
            >
              See all Transactions
            </Button>
          </header>
          <main className={styles.payment_provider_container}>
            <div className={styles.selector_container}>
              <div className={styles.provider_selector_container}>
                <label htmlFor="payment_provider">
                  Select Payment Provider
                </label>
                <Select
                  placeholder={
                    isLoadingTransferProviders
                      ? "Fetching Providers..."
                      : "Select Provider"
                  }
                  size="large"
                  disabled={isLoadingTransferProviders}
                  className="w-full"
                  onChange={(value: number) => {
                    setSelectedProviderDetails((prevState) => ({
                      ...prevState,
                      id: value,
                    }));
                  }}
                >
                  {transferProviders?.data?.providers?.map(
                    (provider: { id: number; name: string }) => (
                      <Option value={provider.id}>{provider.name}</Option>
                    ),
                  )}
                </Select>
              </div>
              {selectedProviderDetails?.id === 2 ? (
                <div className={styles.bank_selector_container}>
                  <label htmlFor="bank_code">Select Bank</label>
                  <Select
                    placeholder={
                      isLoadingEasyPayFinancialInstitutions
                        ? "Loading..."
                        : "Select Bank"
                    }
                    className="w-full"
                    id="bank_code"
                    size="large"
                    value={selectedBankCode}
                    onChange={(value: string) => setSelectedBankCode(value)}
                  >
                    {easyPayBankList?.map((bank: Bank) => (
                      <Option value={bank.bankCode}>{bank.bankName}</Option>
                    ))}
                  </Select>
                </div>
              ) : null}
            </div>
            {selectedProviderDetails ? (
              <div className={styles.provider_details_container}>
                <div className={styles.provided_name_container}>
                  <p className={styles.provider_name}>
                    {selectedProviderDetails?.name}
                  </p>
                  <div className={styles.switcher}>
                    <label
                      htmlFor="current_payment_provider"
                      className={styles.switch_label}
                    >
                      Current payment provider
                    </label>

                    <Switch
                      id="current_payment_provider"
                      checked={
                        transferProviders?.data
                          ?.currentFundsTransferProviderId ===
                        selectedProviderDetails?.id
                      }
                      disabled={
                        transferProviders?.data
                          ?.currentFundsTransferProviderId ===
                        selectedProviderDetails?.id
                      }
                      onChange={() => {
                        setOpenSetCurrentProviderModal(true);
                      }}
                    />
                  </div>
                </div>
                <div className={styles.provider_account_details}>
                  <header className={styles.details_header}>
                    <h3>Bank Details</h3>
                    <div
                      className={styles.reveal}
                      onClick={() => setIsReveal(!isReveal)}
                    >
                      <img
                        src={isReveal ? eyeSlash : eye}
                        alt="eye slash"
                        className={styles.eye_icon}
                        role={"button"}
                        aria-roledescription={"reveal"}
                      />
                      <p className={styles.reveal_txt}>
                        {isReveal ? "Hide Details" : "See Details"}
                      </p>
                    </div>
                  </header>
                  <div className={styles.details_body_container}>
                    <div className={styles.account_balance_limits_container}>
                      <div className={styles.account_balance_wrapper}>
                        <p className={styles.business_account_balance_text}>
                          Business Account Balance
                        </p>
                        {isLoadingAccountBalance ? (
                          <Loader />
                        ) : (
                          <p className={styles.business_account_balance_value}>
                            ₦
                            {isReveal
                              ? accountBalanceData?.data?.availableBalance
                                ? formatMoney(
                                    accountBalanceData?.data?.availableBalance,
                                  )
                                : "0.00"
                              : "************"}
                          </p>
                        )}
                      </div>
                      <div className={styles.limit_container}>
                        <div>
                          <div>
                            <p className={styles.limit_text}>
                              Minimum Transfer
                            </p>
                            <p className={styles.business_minimum_limit_value}>
                              ₦
                              {isReveal
                                ? selectedProviderDetails?.minimumTransferAmount
                                  ? formatMoney(
                                      selectedProviderDetails.minimumTransferAmount,
                                    )
                                  : "0.00"
                                : "************"}
                            </p>
                          </div>
                          <div>
                            <p className={styles.limit_text}>
                              Maximum Transfer
                            </p>
                            <p className={styles.business_maximum_limit_value}>
                              ₦
                              {isReveal
                                ? selectedProviderDetails?.maximumTransferAmount
                                  ? formatMoney(
                                      selectedProviderDetails?.maximumTransferAmount,
                                    )
                                  : "0.00"
                                : "************"}
                            </p>
                          </div>
                        </div>
                        <div
                          className={styles.edit_limit_icon_container}
                          onClick={() => setOpenEditLimitModal(true)}
                          role={"button"}
                          aria-roledescription={"edit limit"}
                        >
                          <img
                            src={editIcon}
                            alt="edit icon"
                            className={styles.eye_icon}
                          />
                          <p className={styles.reveal_txt}>Edit limit</p>
                        </div>
                      </div>
                    </div>
                    {Array.isArray(selectedProviderDetails?.accounts) &&
                    selectedProviderDetails?.accounts.length > 0 ? (
                      <div className={styles.current_bank_details_container}>
                        <div className={styles.current_bank_details}>
                          <span
                            className={
                              selectedAccountInfo?.isSetProviderAccount
                                ? styles.active_status
                                : styles.inactive_status
                            }
                          >
                            {selectedAccountInfo?.isSetProviderAccount
                              ? "Active"
                              : "Inactive"}
                          </span>
                          <p className={styles.business_account_balance_text}>
                            Current Bank provider
                          </p>
                          <p className={styles.bank_name}>
                            {selectedAccountInfo?.bankName}
                          </p>
                        </div>
                        <div className={styles.current_bank_details}>
                          <p className={styles.business_account_balance_text}>
                            Account Number
                          </p>
                          <p className={styles.business_maximum_limit_value}>
                            {isReveal
                              ? selectedAccountInfo?.accountNumber
                              : "************"}
                          </p>
                        </div>
                        <div>
                          <p className={styles.business_account_balance_text}>
                            Account Name
                          </p>
                          <p className={styles.business_maximum_limit_value}>
                            {isReveal
                              ? selectedAccountInfo?.accountName
                              : "************"}
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </main>

          <ConfirmModal
            open={openSetCurrentProviderModal}
            handleOk={handleSetCurrentProvider}
            handleCancel={() => setOpenSetCurrentProviderModal(false)}
            title="Set Provider"
            confirmBtnText="Activate"
            cancelBtnTxt="No"
            isLoading={isLoading}
          />

          <EditTransferLimit
            open={openEditLimitModal}
            handleCancel={() => setOpenEditLimitModal(false)}
            title="Edit Transfer Limit"
            confirmBtnText="Update"
            cancelBtnTxt="No"
            easyPayBankList={easyPayBankList}
            selectedProviderDetails={selectedProviderDetails}
          />
        </>
      )}
    </div>
  );
};

export default TransactionSettings;
